import { Component } from '@theme/component';
import { DrawingApi, DrawingCart, DrawingError, uuid, validId, cropGeometry, readyVersion, approvedPayload, cartPayload, productBinding } from '@theme/trekka-drawing-core';

/** @extends {Component<{source:HTMLCanvasElement,status:HTMLElement,config:HTMLScriptElement,editor:HTMLElement,result:HTMLElement,resultImage:HTMLImageElement,resultPreview:HTMLElement,previewLoader:HTMLElement,originalImage:HTMLImageElement,versions:HTMLElement,generate:HTMLButtonElement,approve:HTMLButtonElement,add:HTMLButtonElement,confirm:HTMLInputElement,adult:HTMLInputElement,quantity:HTMLInputElement}>} */
class TrekkaDrawing extends Component {
  #life; #work; #bitmap; #design; #selected; #seen; #busy = false; #rotation = 0;
  #crop = { x: 0, y: 0, width: 1, height: 1 }; #intent = 0; #previewPending = false;
  #draftRequest = uuid(); #generationRequest; #blob; #api; #cart; #strings; #variants; #timer;
  #storageKey; #config; #configText; #sourceValid = false; #sessionReady = false; #productId;
  requiredRefs = ['source','status','config','editor','result','resultImage','resultPreview','previewLoader','originalImage','versions','generate','approve','add','confirm','adult','quantity'];

  connectedCallback() {
    super.connectedCallback();
    if (this.#life) return;
    this.#life = new AbortController();
    this.#configText = this.refs.config.textContent;
    this.#config = JSON.parse(this.#configText);
    this.#strings = this.#config.strings;
    const binding = productBinding(this.#config);
    this.#variants = binding.variants; this.#productId = binding.productId;
    this.#storageKey = 'trekka-drawing:' + (this.#productId || 'unconfigured');
    this.#cart = new DrawingCart(this.#config.root);
    this.#api = undefined;
    try { if (this.#config.enabled) this.#api = new DrawingApi(this.#config.apiUrl); } catch { this.#api = undefined; }
    this.dataset.ready = 'true';
    this.querySelector('[data-loading]').hidden = true;
    this.querySelector('[data-controls]').hidden = false;
    this.#controls();
    if (this.#api) this.#run(signal => this.#connect(signal), 'loading');
  }
  updatedCallback() {
    super.updatedCallback();
    if (this.refs.config.textContent !== this.#configText) {
      this.disconnectedCallback(); this.#design = undefined; this.#selected = undefined; this.#seen = undefined;
      this.refs.editor.hidden = true; this.refs.result.hidden = true; this.refs.confirm.checked = false;
      this.connectedCallback(); return;
    }
    this.#controls(); if (this.#bitmap) this.#draw();
  }
  disconnectedCallback() {
    super.disconnectedCallback(); this.#life?.abort(); this.#life = undefined; this.#work?.abort();
    clearTimeout(this.#timer); this.#bitmap?.close(); this.#bitmap = undefined; this.#work = undefined; this.#busy = false; this.#previewPending = false; this.#blob = undefined; this.#sourceValid = false; this.#sessionReady = false; this.#intent++;
  }
  async #connect(signal) {
    this.#sessionReady = false;
    const session = await this.#api.session(signal);
    if (session.ready !== true) throw new DrawingError('service_unavailable');
    this.#sessionReady = true;
    let saved; try { saved = sessionStorage.getItem(this.#storageKey); } catch { /* Storage is optional. */ }
    if (this.#productId && validId(saved)) { this.#design = await this.#api.view(saved, signal); await this.#poll(signal); }
    else this.#status('choose');
    this.#controls();
  }
  #text(key) { return this.#strings[key] || this.#strings.generic; }
  #status(key, error = false) { this.refs.status.textContent = this.#text(key); this.refs.status.dataset.error = String(error); }
  #remember(id) { try { if (id) sessionStorage.setItem(this.#storageKey,id); else sessionStorage.removeItem(this.#storageKey); } catch { /* Cookies still own the server draft. */ } }
  #controls() {
    if (!this.#strings) return;
    const approved = Boolean(this.#design?.approved_version_id), active = this.#design?.versions?.some(v => ['queued','processing'].includes(v.status));
    this.querySelectorAll('[data-edit]').forEach(el => { el.disabled = this.#busy || Boolean(this.#design); });
    this.querySelectorAll('[data-new]').forEach(el => { el.disabled = this.#busy; });
    this.refs.generate.disabled = !this.#api || !this.#sessionReady || !this.#productId || this.#config.generationEnabled !== true || this.#busy || approved || active || (!this.#design && (!this.#sourceValid || !this.refs.adult.checked)) || (this.#design && this.#design.attempts_remaining < 1) || (this.#design?.status === 'uploading' && !this.#sourceValid && !this.#blob);
    this.refs.previewLoader.hidden = !this.#previewPending;
    this.refs.resultPreview.setAttribute('aria-busy', String(this.#previewPending));
    this.refs.resultImage.hidden = this.#previewPending || !this.#selected || this.#seen !== this.#selected;
    this.refs.originalImage.hidden = !this.refs.originalImage.hasAttribute('src');
    this.refs.generate.textContent = this.#text(this.#generationRequest ? 'retry_connection' : this.#design?.versions?.length ? 'regenerate' : 'generate');
    this.refs.approve.disabled = this.#busy || approved || !this.#selected || this.#seen !== this.#selected || !this.refs.confirm.checked;
    this.refs.confirm.disabled = this.#busy || approved || this.#seen !== this.#selected || !this.#selected;
    this.refs.versions.querySelectorAll('input').forEach(el => { el.disabled = this.#busy || approved; });
    this.refs.add.disabled = this.#busy || !this.#sessionReady || !approved || this.#variants.length !== 1;
    this.refs.quantity.disabled = this.refs.add.disabled;
    this.querySelector('[data-approval]').textContent = approved ? this.#text('approved') : '';
    this.querySelector('[data-unavailable]').hidden = Boolean(this.#api && this.#sessionReady && this.#config.generationEnabled === true);
    this.querySelector('[data-purchase-unavailable]').hidden = this.#variants.length === 1;
    this.querySelector('[data-remaining]').textContent = this.#design ? this.#text('remaining').replace('{count}', this.#design.attempts_remaining) : '';
    this.setAttribute('aria-busy', String(this.#busy));
  }
  /** @param {Event} event */
  async chooseFile(event) {
    const file = event.target.files?.[0]; if (!file || this.#busy || this.#design) return;
    const intent = ++this.#intent;
    this.#sourceValid = false; this.#status('loading'); this.#controls();
    let bitmap;
    try {
      if (!['image/png','image/jpeg','image/webp'].includes(file.type) || file.size < 1 || file.size > 10000000) throw new DrawingError('invalid_file');
      bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      if (intent !== this.#intent || !this.isConnected) { bitmap.close(); return; }
      if (bitmap.width * bitmap.height > 24000000 || Math.min(bitmap.width,bitmap.height) < 32 || Math.max(bitmap.width,bitmap.height) > 12000) throw new DrawingError('invalid_image');
      this.#bitmap?.close(); this.#bitmap = bitmap; bitmap = undefined;
      this.refs.adult.checked = false;
      this.#rotation = 0; this.#crop = {x:0,y:0,width:1,height:1}; this.#blob = undefined; this.#draftRequest = uuid();
      this.querySelectorAll('[data-crop]').forEach(input => { input.value = ['width','height'].includes(input.dataset.crop) ? 100 : 0; this.#output(input); });
      this.refs.editor.hidden = false; this.#draw(); this.#status('selected');
    } catch (error) { bitmap?.close(); this.#status(error.code || 'invalid_image',true); }
    finally { if (intent === this.#intent) this.#controls(); }
  }
  rotate() { if (!this.#bitmap || this.#design || this.#busy) return; this.#rotation = (this.#rotation + 90) % 360; this.#changed(); }
  /** @param {Event} event */
  crop(event) {
    const input = event.target; if (!input.dataset.crop || !this.#bitmap || this.#design || this.#busy) return;
    const value = Math.max(0,Math.min(100,Number(input.value))); this.#crop[input.dataset.crop] = value / 100;
    this.#crop.width = Math.min(Math.max(.01,this.#crop.width),1-this.#crop.x);
    this.#crop.height = Math.min(Math.max(.01,this.#crop.height),1-this.#crop.y);
    this.querySelectorAll('[data-crop]').forEach(el => { el.value = Math.round(this.#crop[el.dataset.crop]*100); this.#output(el); }); this.#changed();
  }
  #output(input) { input.nextElementSibling.value = input.value + '%'; }
  #changed() { this.#blob = undefined; this.#draftRequest = uuid(); this.#draw(); this.#status('remark'); this.#controls(); }
  #draw() {
    this.#sourceValid = false; if (!this.#bitmap) return;
    try {
      const b = this.#bitmap, g = cropGeometry(b.width,b.height,this.#rotation,this.#crop), ctx = this.refs.source.getContext('2d');
      ctx.resetTransform(); ctx.fillStyle = '#fff'; ctx.fillRect(0,0,1024,1024); ctx.save();
      ctx.translate(g.left,g.top); ctx.scale(g.scale,g.scale); ctx.translate(-g.x,-g.y);
      if (this.#rotation === 90) { ctx.translate(b.height,0); ctx.rotate(Math.PI/2); }
      if (this.#rotation === 180) { ctx.translate(b.width,b.height); ctx.rotate(Math.PI); }
      if (this.#rotation === 270) { ctx.translate(0,b.width); ctx.rotate(3*Math.PI/2); }
      ctx.drawImage(b,0,0); ctx.restore(); this.#sourceValid = true;
    } catch (error) { this.#status(error.code || 'invalid_image',true); }
  }
  confirm() { this.#controls(); }
  #showPending() {
    this.#previewPending = true;
    this.refs.result.hidden = false;
    if (this.#bitmap && !this.#design?.source_ready) this.refs.originalImage.src = this.refs.source.toDataURL('image/png');
    this.#controls();
  }
  async #run(fn, message = 'loading') {
    if (this.#busy) return; this.#busy = true; this.#work = new AbortController(); const work = this.#work;
    const abort = () => work.abort(); this.#life.signal.addEventListener('abort',abort,{once:true});
    this.#status(message); this.#controls();
    try { await fn(work.signal); }
    catch (error) { if (this.isConnected && error.code !== 'cancelled') this.#status(error.code || 'generic',true); }
    finally { this.#life?.signal.removeEventListener('abort',abort); if (this.#work === work) { this.#busy = false; this.#previewPending = false; this.#controls(); } }
  }
  async #pause(signal) {
    await new Promise((resolve,reject) => {
      const abort = () => { clearTimeout(this.#timer); reject(new DrawingError('cancelled')); };
      if (signal.aborted) return abort();
      signal.addEventListener('abort',abort,{once:true});
      this.#timer = setTimeout(() => { signal.removeEventListener('abort',abort); resolve(); },2000);
    });
  }
  async #poll(signal) {
    this.#showPending();
    for (let count=0;count<150;count++) {
      this.#design = await this.#api.view(this.#design.id,signal);
      const active = this.#design.status === 'preparing' || this.#design.versions.some(v=>['queued','processing'].includes(v.status));
      if (!active) { await this.#renderDesign(signal); return; }
      await this.#pause(signal);
    }
    throw new DrawingError('processing_later');
  }
  generate() {
    if (this.refs.generate.disabled) return;
    this.#run(async signal => {
      this.#showPending();
      if (!this.#design || this.#design.status === 'uploading') {
        const blob = this.#blob || await new Promise(resolve => this.refs.source.toBlob(resolve,'image/png'));
        if (!blob) throw new DrawingError('invalid_image'); this.#blob = blob;
        await this.#api.session(signal);
        this.#design = await this.#api.create({request_id:this.#draftRequest,adult_acknowledged:this.refs.adult.checked,content_type:'image/png',byte_size:blob.size,transform:{rotation:0}},signal);
        this.#remember(this.#design.id);
        if (this.#design.upload) { await this.#api.upload(this.#design.upload,blob,signal); this.#design = await this.#api.complete(this.#design.id,signal); }
      }
      if (this.#design.status === 'uploading') throw new DrawingError('upload_incomplete');
      if (!this.#design.source_ready) await this.#poll(signal);
      if (!this.#design.source_ready || this.#design.status !== 'ready') throw new DrawingError(this.#design.error || 'source_not_ready');
      this.#generationRequest ||= uuid();
      const queued = await this.#api.generate(this.#design.id,this.#generationRequest,signal);
      this.#generationRequest = undefined; this.#selected = queued.id; this.#status('generating'); await this.#poll(signal);
    },'uploading');
  }
  refresh() { if (this.#api) this.#run(signal=>this.#design && this.#sessionReady ? this.#poll(signal) : this.#connect(signal),'loading'); }
  async #renderDesign(signal) {
    if (!this.#design) return;
    this.refs.editor.hidden = Boolean(this.#design.source_ready);
    this.refs.result.hidden = !this.#design.source_ready && !this.#previewPending;
    if (this.#design.source_ready) {
      const original = await this.#api.preview(this.#design.id,'source',signal); this.#setImage(this.refs.originalImage,original.url);
      const ready = this.#design.versions.filter(v=>v.status==='ready');
      if (this.#design.approved_version_id) this.#selected = this.#design.approved_version_id;
      if (!ready.some(v=>v.id===this.#selected)) this.#selected = ready.at(-1)?.id;
      const fragment = document.createDocumentFragment();
      ready.forEach(version=>{ const label=document.createElement('label'),radio=document.createElement('input'),text=document.createTextNode(this.#text('version').replace('{number}',version.number));radio.type='radio';radio.name='version-'+this.#config.sectionId;radio.value=version.id;radio.setAttribute('on:change','/selectVersion');radio.checked=version.id===this.#selected;radio.disabled=Boolean(this.#design.approved_version_id);label.append(radio,text);fragment.append(label); });
      this.refs.versions.replaceChildren(fragment);
      if (this.#selected) await this.#showVersion(signal);
      const failed = this.#design.versions.at(-1);
      this.#status(failed && ['failed','rejected'].includes(failed.status) ? failed.error || 'generic' : ready.length ? 'result_ready' : 'source_ready', Boolean(failed && ['failed','rejected'].includes(failed.status)));
    } else this.#status(this.#design.error || 'source_not_ready',true);
    this.#controls();
  }
  #setImage(image,value) {
    const url = new URL(value); if(url.protocol!=='https:'||url.username||url.password)throw new DrawingError('preview_not_ready');
    image.referrerPolicy='no-referrer';image.src=url.href;return url.href;
  }
  async #showVersion(signal) {
    this.#seen = undefined; this.refs.confirm.checked = false;
    this.#showPending();
    const id=this.#selected, version=readyVersion(this.#design,id), preview=await this.#api.preview(this.#design.id,id,signal);
    const url=this.#setImage(this.refs.resultImage,preview.url);
    const colors=this.querySelector('[data-colors]');colors.replaceChildren();
    version.palette.forEach(color=>{const item=document.createElement('li'),swatch=document.createElement('span');swatch.style.backgroundColor=color;swatch.setAttribute('aria-hidden','true');item.append(swatch,document.createTextNode(this.#strings['color_'+color.slice(1).toLowerCase()] || color));colors.append(item);});
    this.#controls();
    try { await this.refs.resultImage.decode(); if(this.#selected===id&&this.refs.resultImage.src===url&&!signal.aborted){this.#seen=id;this.#previewPending=false;this.#controls();} }
    catch { if(!signal.aborted)throw new DrawingError('preview_not_ready'); }
  }
  /** @param {Event} event */
  selectVersion(event) {
    if (this.#busy || this.#design?.approved_version_id || !(event.target instanceof HTMLInputElement)) return;
    this.#selected = event.target.value; this.#run(signal=>this.#showVersion(signal),'loading');
  }
  approve() {
    if (this.refs.approve.disabled) return;
    this.#run(async signal=>{
      const payload=approvedPayload(this.#design,this.#selected,this.refs.confirm.checked && this.#seen===this.#selected);
      await this.#api.approve(this.#design.id,payload,signal);this.#design=await this.#api.view(this.#design.id,signal);await this.#renderDesign(signal);this.#status('approved');
    },'approving');
  }
  add() {
    if (this.refs.add.disabled) return;
    this.#run(async signal=>{
      const quantity=Number(this.refs.quantity.value);if(!Number.isInteger(quantity)||quantity<1||quantity>20)throw new DrawingError('invalid_quantity');
      const current=await this.#cart.current(signal),reservation=await this.#api.reserve(this.#design.id,current.token,quantity,signal);
      const payload=cartPayload(reservation,this.#design,this.#selected,this.#variants,quantity);
      await this.#cart.add(payload,signal);window.location.assign(this.#config.root+'cart');
    },'adding');
  }
  reset() {
    if(this.#busy)return;this.#intent++;this.#bitmap?.close();this.#bitmap=undefined;this.#design=undefined;this.#selected=undefined;this.#seen=undefined;this.#blob=undefined;this.#sourceValid=false;this.#previewPending=false;this.#generationRequest=undefined;this.#draftRequest=uuid();this.#remember(null);
    this.querySelectorAll('input[type=file]').forEach(el=>{el.value='';});this.refs.adult.checked=false;this.refs.confirm.checked=false;
    this.refs.editor.hidden=true;this.refs.result.hidden=true;this.refs.resultImage.removeAttribute('src');this.refs.originalImage.removeAttribute('src');this.#status('choose');this.#controls();
  }
}
if (!customElements.get('trekka-drawing')) customElements.define('trekka-drawing',TrekkaDrawing);
